// De velden die een handeling in de materialentabel op een AdressMaterials-regel
// schrijft: een nieuwe regel, 'Mee te nemen', 'Gebruikt' of 'Status'.
//
// Nodig omdat dezelfde regel twee vormen heeft (oud: alleen `Quantity`; nieuw:
// `QuantityPlanned` + `QuantityUsed`) en de STATUS gekoppeld is aan 'Gebruikt'.
// Die koppeling op één plek houden voorkomt dat het huishouden en de afspraak
// het elk anders doen. De voorraad volgt via materialStockDelta(regel, {...regel, ...patch}).
//
// Besluit Gijs 2026-10-13: STATUS blijft (Hilversum zet er 135 regels per kwartaal
// mee op 'Gebruikt'). Status op 'Gebruikt' vult een lege 'Gebruikt' met 'Mee te
// nemen' — één klik, cijfers gelijk. Een oude regel die niemand aanraakt blijft
// zoals hij is; alleen 'Gebruikt' invullen maakt er een nieuwe regel van.
// AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10, M4)

import materialQuantities from './materialQuantities'

/**
 * @typedef {object} AdressMaterialEntry
 * @property {number} [Quantity] - Oude vorm: het ene aantal
 * @property {number} [QuantityPlanned] - Mee te nemen
 * @property {number | null} [QuantityUsed] - Gebruikt; null = leeg
 * @property {'toDo' | 'done'} [Status]
 * @property {boolean} [Deducted] - Of het aantal van de voorraad af ging
 * @property {string} [TaskID] - De afspraak waarvandaan de regel is toegevoegd
 */

/**
 * @param {number} aantal - Mee te nemen
 * @param {string} [taskID] - De afspraak waarvandaan het materiaal is toegevoegd
 * @returns {{ Quantity: number, QuantityPlanned: number, QuantityUsed: null, Status: 'toDo', TaskID: string }}
 */
const newEntryQuantities = (aantal, taskID = '') => ({
    Quantity: aantal,
    QuantityPlanned: aantal,
    QuantityUsed: null,
    Status: 'toDo',
    TaskID: taskID || '',
})

/**
 * @param {AdressMaterialEntry} regel - De huidige AdressMaterials-regel
 * @param {number} aantal - Nieuw aantal mee te nemen
 * @returns {object}
 */
const plannedPatch = (regel, aantal) => (materialQuantities(regel).legacy
    ? { Quantity: aantal }
    // Quantity loopt mee: het is het oude veld, en wie het nog leest ziet zo het geplande aantal.
    : { QuantityPlanned: aantal, Quantity: aantal })

/**
 * @param {AdressMaterialEntry} regel
 * @param {number | null} gebruikt - null = leeg
 * @returns {object}
 */
const usedPatch = (regel, gebruikt) => {
    const status = gebruikt === null ? 'toDo' : 'done'
    const huidig = materialQuantities(regel)
    return huidig.legacy
        ? { QuantityPlanned: huidig.planned, QuantityUsed: gebruikt, Status: status }
        : { QuantityUsed: gebruikt, Status: status }
}

/**
 * @param {AdressMaterialEntry} regel
 * @param {'toDo' | 'done'} status
 * @returns {object}
 */
const statusPatch = (regel, status) => {
    const huidig = materialQuantities(regel)
    if (huidig.legacy) return { Status: status }
    if (status === 'done') return { Status: 'done', QuantityUsed: huidig.used === null ? huidig.planned : huidig.used }
    return { Status: 'toDo', QuantityUsed: null }
}

export { newEntryQuantities, plannedPatch, usedPatch, statusPatch }

// Stijlregel 6: een default export onderaan. Vier losse handelingen, dus het
// geheel als object; aanroepers gebruiken de named exports.
const materialEntryPatch = { newEntryQuantities, plannedPatch, usedPatch, statusPatch }

export default materialEntryPatch
